import { cva } from 'class-variance-authority';
import clsx from 'clsx';

import { getChannelIconName } from '@/utils/channel-icon';

import { LetterAvatar } from './letter-avatar';
import { type TypographyVariant, Typography } from './typography';

type IconSize = 'xs' | 'sm' | 'md' | 'lg';

/** Maps icon size to a proportional typography variant for the name label. */
const typographyBySize: Record<IconSize, TypographyVariant> = {
  xs: 'small2',
  sm: 'body2',
  md: 'large2',
  lg: 'large2',
};

/** Eagerly resolved map of all channel icon SVGs keyed by path. */
const channelIcons: Record<string, { default: string }> = import.meta.glob(
  '@/assets/icons/channels/*/*.svg',
  { eager: true },
);

/**
 * Variant styles for the channel icon.
 * - small: fixed square (w matches h)
 * - large: fixed height, auto width (preserves aspect ratio)
 */
const channelIconVariants = cva('object-contain', {
  variants: {
    variant: {
      small: '',
      large: 'w-auto',
    },
    size: {
      xs: 'h-4',
      sm: 'h-5',
      md: 'h-6',
      lg: 'h-8',
    },
  },
  compoundVariants: [
    { variant: 'small', size: 'xs', className: 'w-4' },
    { variant: 'small', size: 'sm', className: 'w-5' },
    { variant: 'small', size: 'md', className: 'w-6' },
    { variant: 'small', size: 'lg', className: 'w-8' },
  ],
  defaultVariants: {
    variant: 'small',
    size: 'md',
  },
});

/** Returns the icon src for a recognized channel, or null for unknown slugs. */
function getIconSrc(iconVariant: 'small' | 'large', slug?: string) {
  if (!slug) return null;

  const iconName = getChannelIconName(slug.toLowerCase());
  if (!iconName) return null;

  return (
    channelIcons[`/src/assets/icons/channels/${iconVariant}/${iconName}.svg`]
      ?.default ?? null
  );
}

interface ChannelIconProps {
  channelSlug: string;
  channelName?: string;
  /** small = square icon, large = full logo (or icon + name fallback) */
  variant?: 'small' | 'large';
  /** Controls the rendered dimensions of the icon */
  size?: IconSize;
  className?: string;
}

/**
 * Renders a channel icon by slug.
 *
 * Resolution order:
 * 1. Matched SVG for the requested variant (large = full logo, small = square icon)
 * 2. Large variant without a wide logo falls back to compact icon + name label
 * 3. Unrecognized channels render a colored letter avatar from the channel name
 */
export function ChannelIcon({
  channelSlug,
  channelName,
  variant = 'small',
  size = 'md',
  className,
}: ChannelIconProps) {
  const iconSrc = getIconSrc(variant, channelSlug);
  const smallIconSrc = getIconSrc('small', channelSlug);

  // 1. Exact SVG match for the requested variant
  if (iconSrc) {
    return (
      <img
        key={channelSlug}
        src={iconSrc}
        alt={channelName ?? 'Channel'}
        className={clsx(channelIconVariants({ variant, size }), className)}
      />
    );
  }

  // 2. Small variant fallback: letter avatar
  if (variant === 'small') {
    return (
      <LetterAvatar name={channelSlug} size={size} className={className} />
    );
  }

  // 3. Large variant without wide logo: compact icon + name label
  if (!channelName) return null;

  const compactIcon = smallIconSrc ? (
    <img
      key={channelSlug}
      src={smallIconSrc}
      alt={channelName}
      className={channelIconVariants({ variant: 'small', size })}
    />
  ) : (
    <LetterAvatar name={channelSlug} size={size} />
  );

  return (
    <div className={clsx('flex items-center gap-1', className)}>
      {compactIcon}
      <Typography variant={typographyBySize[size]}>{channelName}</Typography>
    </div>
  );
}
